import { yupResolver } from "@hookform/resolvers/yup";
import ArrowBack from "@mui/icons-material/ArrowBack";
import LoadingButton from "@mui/lab/LoadingButton";
import { Divider } from "@mui/material";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import defaultAxios, { AxiosError } from "axios";
import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQueryClient } from "react-query";
import Swal from "sweetalert2";
import * as yup from "yup";
import { AddPenyewaCoWork, ErrorFieldResponse } from "../../constants/types";
import { useAuth } from "../../context/authContext";
import axios from "../../services/axios";

interface IModalTambahPenyewaProps {
    isOpenModalTambahPenyewa: boolean;
    closeModalTambahPenyewa: () => void;
    dataPenyewa?: AddPenyewaCoWork;
}

type Body = {
    ukmId: number | undefined;
    namaPerusahaan: string;
    npwpPerusahaan: string;
    nomorTelepon: string;
    alamat: string;
    namaPic: string;
    nomorTeleponPic: string;
    alamatPic: string;
    npwpPic: string;
    noKtpPic: string;
};

const schema = yup.object({
    // ukm: yup
    //     .object()
    //     .shape({
    //         id: yup.string().required("Kolom wajib diisi"),
    //         nama: yup.string().required("Kolom wajib diisi"),
    //     })
    //     .required("Kolom wajib diisi"),
    namaPerusahaan: yup
        .string()
        .required("Kolom wajib diisi")
        .max(50, "Nama perusahaan terlalu panjang"),
    nomorTelepon: yup
        .string()
        .required("Kolom wajib diisi")
        .matches(/^[0-9]+$/, "Nomor telepon harus berupa angka")
        .min(10, "Nomor telepon terlalu pendek")
        .max(15, "Nomor telepon terlalu panjang"),
    alamat: yup
        .string()
        .required("Kolom wajib diisi")
        .max(200, "Alamat terlalu panjang"),
    namaPic: yup
        .string()
        .required("Kolom wajib diisi")
        .max(50, "Nama PIC terlalu panjang"),
    nomorTeleponPic: yup
        .string()
        .required("Kolom wajib diisi")
        .matches(/^[0-9]+$/, "Nomor telepon harus berupa angka")
        .min(10, "Nomor telepon terlalu pendek")
        .max(15, "Nomor telepon terlalu panjang"),
    alamatPic: yup
        .string()
        .required("Kolom wajib diisi")
        .max(200, "Alamat PIC terlalu panjang"),
    npwpPerusahaan: yup
        .string()
        .nullable()
        .transform((value, originalValue) =>
            originalValue === "" || originalValue === null ? null : value,
        )
        .matches(/^[0-9]*$/, "NPWP harus berupa angka")
        .min(15, "NPWP harus 15 digit")
        .max(15, "NPWP harus 15 digit"),
    npwpPic: yup
        .string()
        .nullable()
        .transform((value, originalValue) =>
            originalValue === "" || originalValue === null ? null : value,
        )
        .matches(/^[0-9]*$/, "NPWP harus berupa angka")
        .min(15, "NPWP harus 15 digit")
        .max(15, "NPWP harus 15 digit"),
    noKtpPic: yup
        .string()
        .nullable()
        .transform((value, originalValue) =>
            originalValue === "" || originalValue === null ? null : value,
        )
        .matches(/^[0-9]*$/, "Nomor KTP harus berupa angka")
        .min(16, "Nomor KTP harus 16 digit")
        .max(16, "Nomor KTP harus 16 digit"),
});

const ModalTambahPenyewa = ({
    isOpenModalTambahPenyewa,
    closeModalTambahPenyewa,
    dataPenyewa,
}: IModalTambahPenyewaProps) => {
    const theme = useTheme();
    const isPhoneScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const [isButtonLoading, setIsButtonLoading] = useState(false);

    const { ukmIdUser } = useAuth();

    const initialValues: AddPenyewaCoWork = React.useMemo(
        () => ({
            id: 0,
            ukmId: ukmIdUser && ukmIdUser[0],
            tanggal: new Date(),
            namaPerusahaan: "",
            nomorTelepon: "",
            alamat: "",
            namaPic: "",
            nomorTeleponPic: "",
            alamatPic: "",
            npwpPerusahaan: "",
            npwpPic: "",
            noKtpPic: "",
        }),
        [ukmIdUser],
    );

    const {
        handleSubmit,
        control,
        formState: { errors },
        setError,
        reset,
    } = useForm<AddPenyewaCoWork>({
        resolver: yupResolver(schema),
        defaultValues: initialValues,
    });

    const queryClient = useQueryClient();
    const mutation = useMutation({
        mutationFn: async (values: {
            body: Body;
            isEdit: boolean;
            id?: number;
        }) => {
            const { body, isEdit, id } = values;

            const formData = new FormData();
            if (body.ukmId !== undefined) {
                formData.append("ukmId", String(body.ukmId));
            }
            formData.append("namaPerusahaan", body.namaPerusahaan);
            formData.append("npwpPerusahaan", body.npwpPerusahaan || "");
            formData.append("nomorTelepon", body.nomorTelepon);
            formData.append("alamat", body.alamat);
            formData.append("namaPic", body.namaPic);
            formData.append("nomorTeleponPic", body.nomorTeleponPic);
            formData.append("alamatPic", body.alamatPic);
            formData.append("npwpPic", body.npwpPic || "");
            formData.append("noKtpPic", body.noKtpPic || "");

            if (isEdit && id) {
                formData.append("_method", "put");
                const res = await axios.post(`/api/mpenyewas/${id}`, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });
                return res.data;
            } else {
                const res = await axios.post(`/api/mpenyewas`, formData, {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                });
                return res.data;
            }
        },
    });

    const onSubmit = (values: AddPenyewaCoWork) => {
        setIsButtonLoading(true);

        const body = {
            ukmId: values.ukmId,
            namaPerusahaan: values.namaPerusahaan,
            npwpPerusahaan: values.npwpPerusahaan,
            nomorTelepon: values.nomorTelepon,
            alamat: values.alamat,
            namaPic: values.namaPic,
            nomorTeleponPic: values.nomorTeleponPic,
            alamatPic: values.alamatPic,
            npwpPic: values.npwpPic,
            noKtpPic: values.noKtpPic,
        };

        mutation.mutate(
            {
                body,
                isEdit: !!dataPenyewa,
                id: dataPenyewa?.id,
            },
            {
                onSuccess: () => {
                    queryClient.invalidateQueries("listPenyewa");
                    Swal.fire({
                        title: dataPenyewa
                            ? "Penyewa berhasil diubah"
                            : "Penyewa berhasil ditambahkan",
                        position: "top-end",
                        showConfirmButton: false,
                        icon: "success",
                        toast: true,
                        timer: 3000,
                        timerProgressBar: true,
                        showCloseButton: true,
                        customClass: {
                            container: "my-swal",
                        },
                    });
                    closeModalTambahPenyewa();
                    reset(initialValues);
                    setIsButtonLoading(false);
                },
                onError: (error) => {
                    if (defaultAxios.isAxiosError(error)) {
                        const serverError = error as AxiosError<
                            ErrorFieldResponse | undefined
                        >;
                        if (serverError && serverError?.response) {
                            const fieldError = serverError?.response?.data;
                            if (fieldError?.errors) {
                                Object.keys(fieldError.errors).forEach(
                                    (key) => {
                                        const errorMessage =
                                            fieldError.errors[key];
                                        setError(
                                            key as keyof typeof initialValues,
                                            {
                                                type: "manual",
                                                message: errorMessage[0],
                                            },
                                        );
                                    },
                                );
                            } else {
                                Swal.fire({
                                    title: `${
                                        fieldError?.message ??
                                        "Terjadi Kesalahan!"
                                    }`,
                                    position: "top-end",
                                    showConfirmButton: false,
                                    icon: "error",
                                    toast: true,
                                    timer: 3000,
                                    timerProgressBar: true,
                                    showCloseButton: true,
                                    customClass: {
                                        container: "my-swal",
                                    },
                                });
                            }
                        }
                    }
                    console.error(error);
                    setIsButtonLoading(false);
                },
            },
        );
    };

    useEffect(() => {
        if (isOpenModalTambahPenyewa) {
            if (dataPenyewa) {
                reset({
                    ...dataPenyewa,
                });
            } else {
                reset(initialValues);
            }
        }
    }, [reset, initialValues, isOpenModalTambahPenyewa, dataPenyewa]);

    return (
        <Dialog
            maxWidth="md"
            fullScreen={isPhoneScreen}
            fullWidth={true}
            open={isOpenModalTambahPenyewa}
            onClose={() => {}}
            scroll="body"
            PaperProps={{
                sx: {
                    borderRadius: 1,
                },
            }}
        >
            <DialogTitle>
                <Stack direction="row" spacing={1} alignItems="center">
                    <IconButton onClick={closeModalTambahPenyewa}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant="h6" fontWeight="bold">
                        {!!!dataPenyewa ? "Tambah Penyewa" : "Ubah Penyewa"}
                    </Typography>
                </Stack>
            </DialogTitle>
            <DialogContent
                dividers
                sx={{ padding: isPhoneScreen ? undefined : 6 }}
            >
                <Stack gap={6}>
                    <Box>
                        <Grid container alignItems="center" spacing={2}>
                            <Grid item xs={12}>
                                <Typography mb={1}>Nama Perusahaan</Typography>

                                <Controller
                                    name="namaPerusahaan"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            id="namaPerusahaan"
                                            inputProps={{
                                                "data-testid":
                                                    "input-namaPerusahaan",
                                            }}
                                            placeholder="Masukan Nama Perusahaan"
                                            fullWidth
                                            variant="outlined"
                                            error={Boolean(
                                                errors.namaPerusahaan,
                                            )}
                                            helperText={
                                                errors.namaPerusahaan
                                                    ? errors.namaPerusahaan
                                                          .message
                                                    : ""
                                            }
                                            {...field}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <Typography mb={1}>
                                    Nomor Telepon Perusahaan
                                </Typography>

                                <Controller
                                    name="nomorTelepon"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            id="nomorTelepon"
                                            inputProps={{
                                                "data-testid":
                                                    "input-nomorTelepon",
                                            }}
                                            placeholder="Nomor Telepon Perusahaan"
                                            fullWidth
                                            variant="outlined"
                                            error={Boolean(errors.nomorTelepon)}
                                            helperText={
                                                errors.nomorTelepon
                                                    ? errors.nomorTelepon
                                                          .message
                                                    : ""
                                            }
                                            {...field}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <Typography mb={1}>NPWP Perusahaan</Typography>

                                <Controller
                                    name="npwpPerusahaan"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            id="npwpPerusahaan"
                                            inputProps={{
                                                "data-testid":
                                                    "input-npwpPerusahaan",
                                            }}
                                            placeholder="Masukan NPWP Perusahaan"
                                            fullWidth
                                            variant="outlined"
                                            error={Boolean(
                                                errors.npwpPerusahaan,
                                            )}
                                            helperText={
                                                errors.npwpPerusahaan
                                                    ? errors.npwpPerusahaan
                                                          .message
                                                    : ""
                                            }
                                            {...field}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography mb={1}>
                                    Alamat Perusahaan
                                </Typography>

                                <Controller
                                    name="alamat"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            id="alamat"
                                            inputProps={{
                                                "data-testid": "input-alamat",
                                            }}
                                            placeholder="Masukan Alamat Perusahaan"
                                            fullWidth
                                            variant="outlined"
                                            error={Boolean(errors.alamat)}
                                            helperText={
                                                errors.alamat
                                                    ? errors.alamat.message
                                                    : ""
                                            }
                                            {...field}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Divider sx={{ my: 2 }} />
                            </Grid>

                            <Grid item xs={6}>
                                <Typography mb={1}>Nama PIC</Typography>

                                <Controller
                                    name="namaPic"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            id="namaPic"
                                            inputProps={{
                                                "data-testid": "input-namaPic",
                                            }}
                                            placeholder="Masukan Nama PIC"
                                            fullWidth
                                            variant="outlined"
                                            error={Boolean(errors.namaPic)}
                                            helperText={
                                                errors.namaPic
                                                    ? errors.namaPic.message
                                                    : ""
                                            }
                                            {...field}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <Typography mb={1}>NPWP PIC</Typography>

                                <Controller
                                    name="npwpPic"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            id="npwpPic"
                                            inputProps={{
                                                "data-testid": "input-npwpPic",
                                            }}
                                            placeholder="Masukan NPWP PIC"
                                            fullWidth
                                            variant="outlined"
                                            error={Boolean(errors.npwpPic)}
                                            helperText={
                                                errors.npwpPic
                                                    ? errors.npwpPic.message
                                                    : ""
                                            }
                                            {...field}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <Typography mb={1}>Nomor KTP PIC</Typography>

                                <Controller
                                    name="noKtpPic"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            id="noKtpPic"
                                            inputProps={{
                                                "data-testid": "input-noKtpPic",
                                            }}
                                            placeholder="Masukan Nomor KTP PIC"
                                            fullWidth
                                            variant="outlined"
                                            error={Boolean(errors.noKtpPic)}
                                            helperText={
                                                errors.noKtpPic
                                                    ? errors.noKtpPic.message
                                                    : ""
                                            }
                                            {...field}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <Typography mb={1}>
                                    Nomor Telepon PIC
                                </Typography>

                                <Controller
                                    name="nomorTeleponPic"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            id="nomorTeleponPic"
                                            inputProps={{
                                                "data-testid":
                                                    "input-nomorTeleponPic",
                                            }}
                                            placeholder="Masukan Nomor Telepon PIC"
                                            fullWidth
                                            variant="outlined"
                                            error={Boolean(
                                                errors.nomorTeleponPic,
                                            )}
                                            helperText={
                                                errors.nomorTeleponPic
                                                    ? errors.nomorTeleponPic
                                                          .message
                                                    : ""
                                            }
                                            {...field}
                                        />
                                    )}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <Typography mb={1}>Alamat PIC</Typography>

                                <Controller
                                    name="alamatPic"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            id="alamatPic"
                                            inputProps={{
                                                "data-testid":
                                                    "input-alamatPic",
                                            }}
                                            placeholder="Masukan Alamat PIC"
                                            fullWidth
                                            variant="outlined"
                                            error={Boolean(errors.alamatPic)}
                                            helperText={
                                                errors.alamatPic
                                                    ? errors.alamatPic.message
                                                    : ""
                                            }
                                            {...field}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Stack>

                <Stack
                    direction="row"
                    spacing={1.5}
                    mt={2}
                    width="100%"
                    sx={{
                        justifyContent: "flex-end",
                        alignItems: "center",
                    }}
                >
                    <LoadingButton
                        loadingPosition="center"
                        variant="outlined"
                        onClick={closeModalTambahPenyewa}
                    >
                        Kembali
                    </LoadingButton>

                    <LoadingButton
                        loading={isButtonLoading}
                        loadingPosition="center"
                        variant="contained"
                        onClick={handleSubmit(onSubmit)}
                        disabled={isButtonLoading}
                        data-testid="simpan-penyewaan"
                    >
                        Simpan
                    </LoadingButton>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

export default ModalTambahPenyewa;
